import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Tabs,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Banner,
  Select,
  Modal,
  ResourceList,
  ResourceItem,
  Thumbnail,
  Badge,
  Spinner,
  Box,
  Collapsible,
  Icon,
  Divider,
  TextField,
} from '@shopify/polaris';
import {
  DragHandleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  ImageIcon,
} from '@shopify/polaris-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiGet, apiPut } from '../api';
import PointsConfigContent from './PointsConfigContent';
import MeditationsContent from './MeditationsContent';
import SubscriptionsContent from './SubscriptionsContent';

// ─── Types ────────────────────────────────────────────────────

interface ShopifyMarket {
  id: string;
  name: string;
  handle: string;
  enabled: boolean;
  primary: boolean;
}

interface ShopifyCollection {
  id: string;
  title: string;
  handle: string;
  image: { url: string; altText: string | null } | null;
  productsCount: number;
}

interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  featuredImage: { url: string; altText: string | null } | null;
  status: string;
  totalInventory: number;
}

interface CatalogCollection {
  shopifyCollectionId: string;
  title: string;
  enabled: boolean;
  position: number;
  productOrder: string[];
}

// ─── Main Settings Component ─────────────────────────────────

export default function Settings() {
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = [
    { id: 'tienda', content: 'Tienda' },
    { id: 'puntos', content: 'Puntos' },
    { id: 'meditaciones', content: 'Meditaciones' },
    { id: 'suscripciones', content: 'Suscripciones' },
    { id: 'app', content: 'App' },
  ];

  return (
    <Page title="Configuración">
      <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
        <Box paddingBlockStart="400">
          {selectedTab === 0 && <StoreTab />}
          {selectedTab === 1 && <PointsConfigContent />}
          {selectedTab === 2 && <MeditationsContent />}
          {selectedTab === 3 && <SubscriptionsContent />}
          {selectedTab === 4 && <AppTab />}
        </Box>
      </Tabs>
    </Page>
  );
}

// ─── Store Tab ───────────────────────────────────────────────

function StoreTab() {
  const [markets, setMarkets] = useState<ShopifyMarket[]>([]);
  const [selectedMarket, setSelectedMarket] = useState('');
  const [collections, setCollections] = useState<CatalogCollection[]>([]);
  const [availableCollections, setAvailableCollections] = useState<ShopifyCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load markets on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet<{ markets: ShopifyMarket[] }>('/panel-api/shopify/markets');
        setMarkets(data.markets);
        // Select the primary market by default
        const primary = data.markets.find((m) => m.primary);
        if (primary) {
          setSelectedMarket(primary.handle);
        } else if (data.markets.length > 0) {
          setSelectedMarket(data.markets[0].handle);
        }
      } catch (err) {
        setError('Error cargando mercados');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load catalog config when market changes
  useEffect(() => {
    if (!selectedMarket) return;
    (async () => {
      setLoading(true);
      try {
        const data = await apiGet<{ config: { collections: CatalogCollection[] } }>(
          `/panel-api/catalog/${selectedMarket}`,
        );
        setCollections(data.config.collections ?? []);
        setDirty(false);
        setSuccess(false);
      } catch (err) {
        setError('Error cargando catálogo');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedMarket]);

  // Load available Shopify collections for the modal
  const loadAvailableCollections = useCallback(async () => {
    try {
      const data = await apiGet<{ collections: ShopifyCollection[] }>('/panel-api/shopify/collections');
      setAvailableCollections(data.collections);
    } catch (err) {
      setError('Error cargando colecciones de Shopify');
    }
  }, []);

  const handleAddCollectionClick = useCallback(async () => {
    await loadAvailableCollections();
    setModalOpen(true);
  }, [loadAvailableCollections]);

  const handleAddCollection = useCallback((collection: ShopifyCollection) => {
    // Don't add if already present
    if (collections.some((c) => c.shopifyCollectionId === collection.id)) return;

    const newCollection: CatalogCollection = {
      shopifyCollectionId: collection.id,
      title: collection.title,
      enabled: true,
      position: collections.length,
      productOrder: [],
    };
    setCollections((prev) => [...prev, newCollection]);
    setDirty(true);
    setModalOpen(false);
  }, [collections]);

  const handleRemoveCollection = useCallback((shopifyCollectionId: string) => {
    setCollections((prev) => prev.filter((c) => c.shopifyCollectionId !== shopifyCollectionId));
    setDirty(true);
  }, []);

  const handleToggleCollection = useCallback((shopifyCollectionId: string) => {
    setCollections((prev) =>
      prev.map((c) =>
        c.shopifyCollectionId === shopifyCollectionId ? { ...c, enabled: !c.enabled } : c,
      ),
    );
    setDirty(true);
  }, []);

  const handleReorderCollections = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setCollections((prev) => {
      const oldIndex = prev.findIndex((c) => c.shopifyCollectionId === active.id);
      const newIndex = prev.findIndex((c) => c.shopifyCollectionId === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      return reordered.map((c, idx) => ({ ...c, position: idx }));
    });
    setDirty(true);
  }, []);

  const handleUpdateProductOrder = useCallback((shopifyCollectionId: string, productOrder: string[]) => {
    setCollections((prev) =>
      prev.map((c) =>
        c.shopifyCollectionId === shopifyCollectionId ? { ...c, productOrder } : c,
      ),
    );
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!selectedMarket) return;
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await apiPut(`/panel-api/catalog/${selectedMarket}`, { collections });
      setDirty(false);
      setSuccess(true);
    } catch (err) {
      setError('Error guardando catálogo');
    } finally {
      setSaving(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const marketOptions = markets.map((m) => ({
    label: `${m.name}${m.primary ? ' (Principal)' : ''}`,
    value: m.handle,
  }));

  // Filter out already-added collections from available list
  const filteredAvailable = availableCollections.filter(
    (ac) => !collections.some((c) => c.shopifyCollectionId === ac.id),
  );

  return (
    <Layout>
      {success && (
        <Layout.Section>
          <Banner tone="success" onDismiss={() => setSuccess(false)}>
            Catálogo guardado correctamente.
          </Banner>
        </Layout.Section>
      )}
      {error && (
        <Layout.Section>
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Layout.Section>
      )}

      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <Select
              label="Mercado"
              options={marketOptions}
              value={selectedMarket}
              onChange={(v) => setSelectedMarket(v)}
            />
          </BlockStack>
        </Card>
      </Layout.Section>

      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingMd">
                Colecciones
              </Text>
              <InlineStack gap="200">
                <Button icon={PlusIcon} onClick={handleAddCollectionClick}>
                  Añadir colección
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  loading={saving}
                  disabled={!dirty}
                >
                  Guardar
                </Button>
              </InlineStack>
            </InlineStack>

            {loading ? (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            ) : collections.length === 0 ? (
              <Text as="p" tone="subdued">
                No hay colecciones configuradas para este mercado. Añade una colección para empezar.
              </Text>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleReorderCollections}
              >
                <SortableContext
                  items={collections.map((c) => c.shopifyCollectionId)}
                  strategy={verticalListSortingStrategy}
                >
                  <BlockStack gap="200">
                    {collections.map((col) => (
                      <SortableCollectionItem
                        key={col.shopifyCollectionId}
                        collection={col}
                        onToggle={() => handleToggleCollection(col.shopifyCollectionId)}
                        onRemove={() => handleRemoveCollection(col.shopifyCollectionId)}
                        onUpdateProductOrder={(order) =>
                          handleUpdateProductOrder(col.shopifyCollectionId, order)
                        }
                      />
                    ))}
                  </BlockStack>
                </SortableContext>
              </DndContext>
            )}
          </BlockStack>
        </Card>
      </Layout.Section>

      {/* Add Collection Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Añadir colección"
      >
        <Modal.Section>
          {filteredAvailable.length === 0 ? (
            <Text as="p" tone="subdued">
              No hay más colecciones disponibles para añadir.
            </Text>
          ) : (
            <ResourceList
              items={filteredAvailable}
              renderItem={(item) => (
                <ResourceItem
                  id={item.id}
                  onClick={() => handleAddCollection(item)}
                  media={
                    <Thumbnail
                      source={item.image?.url ?? ''}
                      alt={item.image?.altText ?? item.title}
                      size="small"
                    />
                  }
                >
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {item.title}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {item.productsCount} productos · {item.handle}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                </ResourceItem>
              )}
            />
          )}
        </Modal.Section>
      </Modal>
    </Layout>
  );
}

// ─── Sortable Collection Item ────────────────────────────────

// ─── App Tab (Force Update) ─────────────────────────────────

function AppTab() {
  const [minVersionIos, setMinVersionIos] = useState('');
  const [minVersionAndroid, setMinVersionAndroid] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<{ settings: Record<string, unknown> }>('/panel-api/app-settings')
      .then((data) => {
        const strip = (v: unknown) => v ? String(v).replace(/"/g, '') : '';
        // Platform-specific keys, fall back to generic
        const generic = strip(data.settings?.min_app_version);
        setMinVersionIos(strip(data.settings?.min_app_version_ios) || generic);
        setMinVersionAndroid(strip(data.settings?.min_app_version_android) || generic);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await Promise.all([
        apiPut('/panel-api/app-settings', {
          key: 'min_app_version_ios',
          value: minVersionIos.trim(),
        }),
        apiPut('/panel-api/app-settings', {
          key: 'min_app_version_android',
          value: minVersionAndroid.trim(),
        }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Layout.AnnotatedSection
        title="Actualización forzada"
        description="Cuando el número de build de la app instalada sea inferior al mínimo configurado, se mostrará un mensaje bloqueante pidiendo al usuario que actualice. Cada plataforma tiene su propio build number."
      >
        <Card>
          <BlockStack gap="400">
            {saved && (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                <p>Configuración guardada</p>
              </Banner>
            )}
            <TextField
              label="Build mínimo iOS"
              type="number"
              value={minVersionIos}
              onChange={setMinVersionIos}
              autoComplete="off"
              helpText="Build number de iOS (actualmente ~129). Los usuarios con un build inferior verán el mensaje de actualización."
              disabled={loading}
            />
            <TextField
              label="Build mínimo Android"
              type="number"
              value={minVersionAndroid}
              onChange={setMinVersionAndroid}
              autoComplete="off"
              helpText="versionCode de Android (actualmente 1). Los usuarios con un build inferior verán el mensaje de actualización."
              disabled={loading}
            />
            <InlineStack align="end">
              <Button variant="primary" onClick={handleSave} loading={saving}>
                Guardar
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </Layout.AnnotatedSection>
    </Layout>
  );
}

// ─── Sortable Collection Item ─────────────────────────────────

function SortableCollectionItem({
  collection,
  onToggle,
  onRemove,
  onUpdateProductOrder,
}: {
  collection: CatalogCollection;
  onToggle: () => void;
  onRemove: () => void;
  onUpdateProductOrder: (order: string[]) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: collection.shopifyCollectionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [expanded, setExpanded] = useState(false);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const handleExpand = useCallback(async () => {
    if (!expanded && products.length === 0) {
      setLoadingProducts(true);
      try {
        const encodedId = encodeURIComponent(collection.shopifyCollectionId);
        const data = await apiGet<{ products: ShopifyProduct[] }>(
          `/panel-api/shopify/collections/${encodedId}/products`,
        );
        let prods = data.products;

        // Sort by productOrder if available
        if (collection.productOrder.length > 0) {
          const orderMap = new Map(collection.productOrder.map((id, idx) => [id, idx]));
          prods = [...prods].sort((a, b) => {
            const aIdx = orderMap.get(a.id) ?? 9999;
            const bIdx = orderMap.get(b.id) ?? 9999;
            return aIdx - bIdx;
          });
        }

        setProducts(prods);
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoadingProducts(false);
      }
    }
    setExpanded(!expanded);
  }, [expanded, products.length, collection.shopifyCollectionId, collection.productOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleReorderProducts = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setProducts((prev) => {
        const oldIndex = prev.findIndex((p) => p.id === active.id);
        const newIndex = prev.findIndex((p) => p.id === over.id);
        const reordered = arrayMove(prev, oldIndex, newIndex);
        onUpdateProductOrder(reordered.map((p) => p.id));
        return reordered;
      });
    },
    [onUpdateProductOrder],
  );

  return (
    <div ref={setNodeRef} style={style}>
      <Box
        padding="300"
        borderRadius="200"
        background="bg-surface-secondary"
        borderColor="border"
        borderWidth="025"
      >
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <InlineStack gap="300" blockAlign="center">
              <div {...attributes} {...listeners} style={{ cursor: 'grab' }}>
                <Icon source={DragHandleIcon} tone="subdued" />
              </div>
              <Text as="span" variant="bodyMd" fontWeight="semibold">
                {collection.title}
              </Text>
              <Badge tone={collection.enabled ? 'success' : undefined}>
                {collection.enabled ? 'Activa' : 'Inactiva'}
              </Badge>
            </InlineStack>
            <InlineStack gap="200">
              <Button
                size="slim"
                onClick={onToggle}
              >
                {collection.enabled ? 'Desactivar' : 'Activar'}
              </Button>
              <Button
                size="slim"
                icon={expanded ? ChevronUpIcon : ChevronDownIcon}
                onClick={handleExpand}
                accessibilityLabel="Ver productos"
              />
              <Button
                size="slim"
                tone="critical"
                onClick={onRemove}
              >
                Eliminar
              </Button>
            </InlineStack>
          </InlineStack>

          <Collapsible open={expanded} id={`products-${collection.shopifyCollectionId}`}>
            <Box paddingBlockStart="200" paddingInlineStart="800">
              {loadingProducts ? (
                <InlineStack align="center">
                  <Spinner size="small" />
                </InlineStack>
              ) : products.length === 0 ? (
                <Text as="p" tone="subdued">
                  Sin productos en esta colección.
                </Text>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleReorderProducts}
                >
                  <SortableContext
                    items={products.map((p) => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <BlockStack gap="100">
                      {products.map((product) => (
                        <SortableProductItem key={product.id} product={product} />
                      ))}
                    </BlockStack>
                  </SortableContext>
                </DndContext>
              )}
            </Box>
          </Collapsible>
        </BlockStack>
      </Box>
    </div>
  );
}

// ─── Sortable Product Item ───────────────────────────────────

function SortableProductItem({ product }: { product: ShopifyProduct }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box padding="200" borderRadius="100" background="bg-surface">
        <InlineStack gap="300" blockAlign="center">
          <div {...attributes} {...listeners} style={{ cursor: 'grab' }}>
            <Icon source={DragHandleIcon} tone="subdued" />
          </div>
          <Thumbnail
            source={product.featuredImage?.url ?? ''}
            alt={product.featuredImage?.altText ?? product.title}
            size="small"
          />
          <BlockStack>
            <Text as="span" variant="bodySm" fontWeight="medium">
              {product.title}
            </Text>
            <Text as="span" variant="bodySm" tone="subdued">
              {product.status} · Stock: {product.totalInventory}
            </Text>
          </BlockStack>
        </InlineStack>
      </Box>
    </div>
  );
}
